import type {
  AddressType,
  CustomerDetails,
  Paginated,
  PartySummary,
  PersonType,
  RecordStatus,
  SupplierDetails,
} from '@ldembalagens/types';
import { api, type QueryParams } from '@/lib/api-client';

export interface PartyQuery extends QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: RecordStatus;
  personType?: PersonType;
  state?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AddressInput {
  id?: string;
  type?: AddressType;
  label?: string | null;
  street: string;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city: string;
  state: string;
  zipCode?: string | null;
  isDefault?: boolean;
}

export interface ContactInput {
  id?: string;
  name: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  notes?: string | null;
  isPrimary?: boolean;
}

export interface PartyInput {
  code?: string;
  personType: PersonType;
  legalName: string;
  tradeName?: string | null;
  document?: string | null;
  stateRegistration?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  website?: string | null;
  paymentTerms?: string | null;
  notes?: string | null;
  status?: RecordStatus;
  addresses?: AddressInput[];
  contacts?: ContactInput[];
}

export interface CustomerInput extends PartyInput {
  municipalRegistration?: string | null;
  creditLimit?: string | null;
}

export interface SupplierInput extends PartyInput {
  leadTimeDays?: number | null;
  materials?: {
    materialId: string;
    supplierCode?: string | null;
    lastPrice?: string | null;
    leadTimeDays?: number | null;
  }[];
}

export const customersApi = {
  list: (query: PartyQuery) => api.get<Paginated<PartySummary>>('/customers', query),
  get: (id: string) => api.get<CustomerDetails>(`/customers/${id}`),
  create: (input: CustomerInput) => api.post<CustomerDetails>('/customers', input),
  update: (id: string, input: Partial<CustomerInput>) =>
    api.patch<CustomerDetails>(`/customers/${id}`, input),
  remove: (id: string) => api.delete(`/customers/${id}`),
};

export const suppliersApi = {
  list: (query: PartyQuery) => api.get<Paginated<PartySummary>>('/suppliers', query),
  options: () => api.get<{ id: string; code: string; name: string }[]>('/suppliers/options'),
  get: (id: string) => api.get<SupplierDetails>(`/suppliers/${id}`),
  create: (input: SupplierInput) => api.post<SupplierDetails>('/suppliers', input),
  update: (id: string, input: Partial<SupplierInput>) =>
    api.patch<SupplierDetails>(`/suppliers/${id}`, input),
  remove: (id: string) => api.delete(`/suppliers/${id}`),
};

export const customersKeys = {
  all: ['customers'] as const,
  list: (q: PartyQuery) => ['customers', 'list', q] as const,
  detail: (id: string) => ['customers', 'detail', id] as const,
};
export const suppliersKeys = {
  all: ['suppliers'] as const,
  list: (q: PartyQuery) => ['suppliers', 'list', q] as const,
  detail: (id: string) => ['suppliers', 'detail', id] as const,
  options: ['suppliers', 'options'] as const,
};
